import { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import MaterialExtraForm from '../components/MaterialExtraForm';
import MaterialExtraPresetSelector from '../components/MaterialExtraPresetSelector';
import { useToast } from '../hooks/useToast';

const getDisplayName = (name) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    const values = Object.values(name);
    return values.find(v => v) || '';
  }
  return String(name);
};

export default function MaterialExtras() {
  const [materialExtras, setMaterialExtras] = useState([]);
  const [filteredMaterialExtras, setFilteredMaterialExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPresetSelector, setShowPresetSelector] = useState(false);
  const [editingMaterialExtra, setEditingMaterialExtra] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });
  const { showToast } = useToast();

  useEffect(() => {
    fetchMaterialExtras();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialExtras, filters]);

  const fetchMaterialExtras = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/material-extras');
      setMaterialExtras(response.data.data);
    } catch (err) {
      setError('Failed to fetch material extras');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...materialExtras];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(m => {
        const name = getDisplayName(m.name);
        return (
          name?.toLowerCase().includes(search) ||
          m.key?.toLowerCase().includes(search)
        );
      });
    }

    // Sort by displayOrder for proper reordering
    filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    setFilteredMaterialExtras(filtered);
  };

  const handleAdd = () => {
    setEditingMaterialExtra(null);
    setShowPresetSelector(true);
  };

  const handleSelectPreset = (duplicatedMaterialExtra) => {
    setShowPresetSelector(false);
    setEditingMaterialExtra(duplicatedMaterialExtra);
    setShowForm(true);
    showToast('Material extra created from preset. You can now customize it.', 'success');
  };

  const handleCreateFromScratch = () => {
    setShowPresetSelector(false);
    setEditingMaterialExtra(null);
    setShowForm(true);
  };

  const handleEdit = (materialExtra) => {
    setEditingMaterialExtra(materialExtra);
    setShowForm(true);
  };

  const handleDelete = async (materialExtra) => {
    if (!window.confirm(`Are you sure you want to delete "${getDisplayName(materialExtra.name)}"?`)) {
      return;
    }

    try {
      await api.delete(`/auth/material-extras/${materialExtra._id}`);
      fetchMaterialExtras();
    } catch (err) {
      alert('Failed to delete material extra');
      console.error(err);
    }
  };

  const handleDuplicate = async (materialExtra) => {
    try {
      const response = await api.post(`/auth/material-extras/${materialExtra._id}/duplicate?from=farm`);
      setEditingMaterialExtra(response.data.data);
      setShowForm(true);
      showToast('Material extra duplicated. You can now customize it.', 'success');
    } catch (err) {
      alert('Failed to duplicate material extra');
      console.error(err);
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingMaterialExtra;
    if (isEditing) {
      await api.patch(`/auth/material-extras/${editingMaterialExtra._id}`, data);
    } else {
      await api.post('/auth/material-extras', data);
    }
    setShowForm(false);
    setEditingMaterialExtra(null);
    fetchMaterialExtras();
    showToast(isEditing ? 'Material extra updated successfully!' : 'Material extra created successfully!', 'success');

  };

  const handleReorder = async (affectedItems) => {
    try {
      await api.patch('/auth/material-extras/reorder', affectedItems);
      // Update local state optimistically
      const updatedMaterialExtras = materialExtras.map(extra => {
        const affected = affectedItems.find(item => item.id === extra._id);
        if (affected) {
          return { ...extra, displayOrder: affected.displayOrder };
        }
        return extra;
      });
      setMaterialExtras(updatedMaterialExtras);
    } catch (err) {
      alert('Failed to reorder material extras');
      console.error(err);
      fetchMaterialExtras(); // Revert to server state on error
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (val) => {
        if (typeof val === 'object') {
          return Object.keys(val).map((lang) => `<strong>${lang}:</strong> ${val[lang]}`).join('<br/>');
        }
        return val;
      }
    },
    { key: 'key', label: 'Key' },
    {
      key: 'materialCostMultiplier',
      label: 'Cost Multiplier',
      render: (val) => val ? `${Number(val).toFixed(2)}x` : '1.00x'
    },
    {
      key: 'showInSite',
      label: 'Show in Site',
      render: (val) => (val ? '✓' : '✗')
    },
    { key: 'displayOrder', label: 'Order' },
    {
      key: 'createdAt',
      label: 'Created',
      render: (val) => new Date(val).toLocaleDateString()
    }
  ];

  const renderFilters = () => (
    <div className="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        placeholder="Search by name or key..."
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading material extras...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
      {error}
    </div>
  );

  return (
    <div>
      {showPresetSelector ? (
        <MaterialExtraPresetSelector
          onSelectPreset={handleSelectPreset}
          onCreateNew={handleCreateFromScratch}
          onCancel={() => setShowPresetSelector(false)}
        />
      ) : showForm ? (
        <MaterialExtraForm
          materialExtra={editingMaterialExtra}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingMaterialExtra(null);
          }}
        />
      ) : (
        <DataTable
          data={filteredMaterialExtras}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onAdd={handleAdd}
          renderFilters={renderFilters}
          reorderable={true}
          allowHTML={true}
          onReorder={handleReorder}
        />
      )}
    </div>
  );
}
